/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
import api from "@/http/api";
import { useOrderInst } from "@/sharedData/Order";
import type { OrderInstruction, OrderType } from "@/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Drawer from "@univdiam/ui/customComponents/Drawer";
import { AddOrderInstruction, AddOrderInstructionSchema } from "@univdiam/ui/customComponents/forms/AddOrderInstruction";
import AdditionalInstruction from "@univdiam/ui/customComponents/OrderDetail/AdditionalInstruction";
import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { z } from "zod";
import TicketIcon from '@/assets/FeaturedIcon.svg'
import { toast } from "sonner";
import { useSocketStore } from "@/socketStore";
import { useAuthStore } from "@/store";

const orderInstCreation = ({ customer_id, label, value, order_id }: { customer_id: string, label: string, value: string, order_id: string }) => {

  return api.post(`/admin/order/instruction`, { customer_id, label, value, order_id });
}

const orderInstUpdation = (id: string, { label, value }: { label: string, value: string }) => {

  return api.put(`/admin/order/instruction/${id}`, { label, value });
}
const orderInstDeletion = (id: string) => {

  return api.delete(`/admin/order/instruction/${id}`);
}

const OrderAdditionalInstruction = ({ orderDetailData }: { orderDetailData: OrderType }) => {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { customer_id } = useParams();
  const [searchParams] = useSearchParams();
  const order_instructionFromQuery = searchParams.get('order_instruction');
  const [addInstruction, setAddInstuction] = useState(false)

  const handleOpenInstructionForm = () => setAddInstuction(true)

  const [selectedInst, setSelectedInst] = useState<OrderInstruction | null>(null)
  const sendNotification = useSocketStore((s) => s.sendNotification);

  const { mutate: orderInstDelete } = useMutation({
    mutationFn: (id: string) => {

      return orderInstDeletion(id)
    },
    onSuccess: async () => {
      toast.success('Order Instruction deleted!', {
        position: 'top-center'
      })
      queryClient.invalidateQueries({ queryKey: ["order-instruction", orderDetailData?.id] })
      setAddInstuction(false)
    }
  })

  const handleInstructionAction = (type: string, data: OrderInstruction) => {
    if (type === 'edit') {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('order_instruction', data.id);
      navigate({ search: searchParams.toString() }, { replace: true });
      setSelectedInst(data)
    } else if (type === 'delete') {
      orderInstDelete(data.id)
    }
  }

  const { mutate: orderAddInst, isError: isOrderInstError, isPending, error: orderInstError } = useMutation({
    mutationFn: (values: z.infer<typeof AddOrderInstructionSchema>) => {

      if (selectedInst && order_instructionFromQuery) {
        const data = {
          label: values.label,
          value: values.value,
        }
        return orderInstUpdation(order_instructionFromQuery, data)
      } else {
        const data = {
          customer_id: customer_id!,
          label: values.label,
          value: values.value,
          order_id: orderDetailData.id!
        }
        return orderInstCreation(data)
      }

    },
    onSuccess: async ({ data }) => {
      toast.success('Order Instruction added!', {
        position: 'top-center'
      })
      queryClient.invalidateQueries({ queryKey: ["order-instruction", orderDetailData?.id] })
      queryClient.invalidateQueries({ queryKey: ["order-log"] })
      sendNotification({
        action_by: user?.role!,
        action_by_id: user?.id.toString()!,
        action_type: selectedInst ? "additional_instruction_updated" : "additional_instruction_added",
        order_id: data.instruction.order_id,
        title: `${selectedInst ? 'Additional instruction updated' : 'Additional instruction added'}`,
        metadata: {
          order_ref: data.instruction.order_id,
          order_id: orderDetailData.order_id, // #UD type string
          customer_id: data?.instruction.customer_id.toString(),
          adminName: user?.full_name!,
          previousLabel: data?.instruction?.previousLable,
          newLabel: data?.instruction?.label
        },
      });
      setAddInstuction(false)
      setSelectedInst(null)
    }
  })


  const { data: instructions, isLoading: isInstructionsLoading } = useOrderInst(orderDetailData?.id);

  const orderInstruction = instructions?.data?.data as OrderInstruction[]


  return (
    <div>
      <AdditionalInstruction
        AdditionalInstructions={orderInstruction || []}
        isLoading={isInstructionsLoading}
        isAdmin={true}
        handleOpenInstructionForm={handleOpenInstructionForm}
      />

      <Drawer open={addInstruction}
        setOpen={(val) => {
          setAddInstuction(val);
          if (!val) {
            setSelectedInst(null);
          }
          const searchParams = new URLSearchParams(window.location.search);
          searchParams.delete('order_instruction');
          navigate({ search: searchParams.toString() }, { replace: true });
        }} title={
          <p className='flex items-center gap-[8px]'>
            <img src={TicketIcon} alt="Add instruction " />
            Add instruction
          </p>
        }>
        <AddOrderInstruction onSubmit={orderAddInst} isError={isOrderInstError} isLoading={isPending} error={orderInstError} AdditionalInstructions={orderInstruction || []} handleInstructionAction={handleInstructionAction} existedValues={selectedInst ? (() => {
          return {
            label: selectedInst?.label,
            value: selectedInst?.value
          };
        })() : undefined} />
      </Drawer>
    </div>
  );
};

export default OrderAdditionalInstruction;
